import { useState, useEffect, useCallback } from 'react';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Stamp, 
  Settings, 
  Users,
  Gift,
  TrendingUp,
  Euro,
  Package,
  Check,
  Trophy
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StampCardTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('config');
  
  // Config state
  const [config, setConfig] = useState({
    enabled: false,
    name: 'Carte de fidélité',
    stamps_required: 10,
    stamp_condition: 'per_order',
    min_order_amount: null,
    reward_product_id: '',
    reward_product_name: '',
    description: ''
  });
  
  // Stats state
  const [stats, setStats] = useState({
    active_cards: 0,
    total_stamps_distributed: 0,
    total_rewards_claimed: 0,
    active_rewards: 0,
    close_to_reward: 0
  });
  
  // Clients state
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  
  // Products for reward selection
  const [dishes, setDishes] = useState([]);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stampcard/config`);
      setConfig(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement de la configuration');
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stampcard/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      const response = await axios.get(`${API}/stampcard/clients`);
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  const fetchDishes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/menu/dishes`);
      setDishes(response.data || []);
    } catch (err) {
      console.error('Error fetching dishes:', err);
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchStats(), fetchDishes()]);
      setLoading(false);
    };
    fetchAll();
  }, [fetchConfig, fetchStats, fetchDishes]);

  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    }
  }, [activeTab, fetchClients]);

  const handleProductSelect = (productId) => {
    const product = dishes.find(d => d.id === productId);
    if (product) {
      setConfig({
        ...config,
        reward_product_id: productId,
        reward_product_name: product.name
      });
    }
  };

  const handleSaveConfig = async () => {
    if (config.enabled && !config.reward_product_id) {
      toast.error('Veuillez sélectionner le produit offert en récompense');
      return;
    }
    if (config.stamp_condition === 'min_amount' && (!config.min_order_amount || config.min_order_amount <= 0)) {
      toast.error('Le montant minimum doit être positif');
      return;
    }
    if (config.stamps_required < 2) {
      toast.error('Le nombre de tampons doit être au moins 2');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/stampcard/config`, {
        enabled: config.enabled,
        name: config.name,
        stamps_required: parseInt(config.stamps_required) || 10,
        stamp_condition: config.stamp_condition,
        min_order_amount: config.stamp_condition === 'min_amount' ? parseFloat(config.min_order_amount) : null,
        reward_product_id: config.reward_product_id,
        reward_product_name: config.reward_product_name,
        description: config.description || null
      });
      toast.success('Configuration sauvegardée');
      fetchConfig();
      fetchStats();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  // Generate stamp visualization
  const renderStampCard = () => {
    const stamps = [];
    const total = config.stamps_required || 10;
    const filled = Math.min(3, total); // Show first 3 as example filled
    
    for (let i = 0; i < total; i++) {
      stamps.push(
        <div 
          key={i}
          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
            i < filled 
              ? 'bg-amber-500 border-amber-500 text-white' 
              : 'border-gray-300 bg-white'
          }`}
        >
          {i < filled && <Check className="w-4 h-4" />}
        </div>
      );
    }
    return stamps;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Carte à Tampons</h2>
          <p className="text-sm text-gray-500">Fidélisez vos clients avec une carte digitale</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">Carte</span>
          <Switch
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
          <span className={`text-sm font-medium ${config.enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
            {config.enabled ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active_cards}</p>
              <p className="text-xs text-gray-500">Cartes actives</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Stamp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_stamps_distributed}</p>
              <p className="text-xs text-gray-500">Tampons distribués</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total_rewards_claimed}</p>
              <p className="text-xs text-gray-500">Récompenses gagnées</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active_rewards}</p>
              <p className="text-xs text-gray-500">Récompenses en attente</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.close_to_reward}</p>
              <p className="text-xs text-gray-500">Proches de la récompense</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Clients
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Settings */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Paramètres de la carte</h2>
              
              <div className="space-y-6">
                {/* Card Name */}
                <div>
                  <Label>Nom de la carte</Label>
                  <Input
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    placeholder="Ex: Carte Café, Carte Burger..."
                    className="mt-1.5"
                  />
                </div>

                  {/* Stamps Required */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Stamp className="w-4 h-4 text-amber-500" />
                      Nombre de tampons pour la récompense
                    </Label>
                    <Input
                      type="number"
                      min="2"
                      max="20"
                      value={config.stamps_required}
                      onChange={(e) => setConfig({ ...config, stamps_required: e.target.value })}
                      className="w-32 mt-1.5"
                    />
                  </div>

                  {/* Stamp Condition */}
                  <div>
                    <Label>Condition pour obtenir un tampon</Label>
                    <Select
                      value={config.stamp_condition}
                      onValueChange={(val) => setConfig({ ...config, stamp_condition: val })}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_order">À chaque commande</SelectItem>
                        <SelectItem value="min_amount">Commande minimum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {config.stamp_condition === 'min_amount' && (
                    <div>
                      <Label className="flex items-center gap-2">
                        <Euro className="w-4 h-4 text-gray-500" />
                        Montant minimum de commande
                      </Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Input
                          type="number"
                          min="1"
                          step="0.5"
                          value={config.min_order_amount || ''}
                          onChange={(e) => setConfig({ ...config, min_order_amount: e.target.value })}
                          className="w-32"
                        />
                        <span className="text-gray-500">€</span>
                      </div>
                    </div>
                  )}

                  {/* Reward Product */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-emerald-500" />
                      Produit offert en récompense *
                    </Label>
                    <Select
                      value={config.reward_product_id || ''}
                      onValueChange={handleProductSelect}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Sélectionner le produit offert..." />
                      </SelectTrigger>
                      <SelectContent>
                        {dishes.map(dish => (
                          <SelectItem key={dish.id} value={dish.id}>
                            {dish.name} ({dish.price?.toFixed(2)}€)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {config.reward_product_name && (
                      <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {config.reward_product_name}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description (affichée aux clients)</Label>
                    <Input
                      value={config.description || ''}
                      onChange={(e) => setConfig({ ...config, description: e.target.value })}
                      placeholder={`Ex: ${config.stamps_required} commandes = 1 ${config.reward_product_name || 'produit'} offert`}
                      className="mt-1.5"
                    />
                  </div>

                  <Button 
                    onClick={handleSaveConfig} 
                    disabled={saving}
                    className="bg-amber-500 hover:bg-amber-600 rounded-xl"
                  >
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Aperçu de la carte</h2>
                
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                      <Stamp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{config.name || 'Carte de fidélité'}</h3>
                      <p className="text-sm text-gray-600">
                        {config.description || `${config.stamps_required} tampons = ${config.reward_product_name || '1 produit'} offert`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {renderStampCard()}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progression: 3/{config.stamps_required}</span>
                    <span className="text-amber-600 font-medium">
                      Plus que {Math.max(0, config.stamps_required - 3)} !
                    </span>
                  </div>
                  
                  {config.reward_product_name && (
                    <div className="mt-4 p-3 bg-white rounded-xl border border-amber-200">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-emerald-500" />
                        <span className="text-sm font-medium text-gray-900">
                          Récompense : {config.reward_product_name}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-400 mt-4 text-center">
                  Aperçu avec 3 tampons d'exemple
                </p>
              </div>
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="mt-6">
          <div className="bg-white rounded-2xl border border-gray-100">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Clients avec carte active</h2>
              <p className="text-sm text-gray-500">Progression des cartes à tampons</p>
            </div>
            
            {loadingClients ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500" />
              </div>
            ) : clients.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                Aucun client avec une carte pour le moment
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {clients.map(client => (
                  <div key={client.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {client.client_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{client.client_name}</p>
                        <p className="text-sm text-gray-500">{client.client_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Progress bar */}
                      <div className="w-32">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{client.current_stamps}/{client.stamps_required}</span>
                          <span>{client.progress_percent}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-amber-500 rounded-full transition-all"
                            style={{ width: `${client.progress_percent}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {client.rewards_claimed} récompense{client.rewards_claimed > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
